import { useState, useEffect, useCallback } from 'react';
import useDebounce from './useDebounce';
import useMeetingsStoreRepository from '../store/meetingsStoreRepository';
import { MEETING_DATE_PRESETS } from '../constants/meetingDatePresets';

/**
 * Custom hook for managing meetings filter state
 * Handles search (with debounce) and simple select filters.
 * Multi-select filters (participants, types, statuses) are now managed
 * directly by the store using combined setters (like IDP pattern).
 */
const useMeetingsRepositorySearch = () => {
  // Local UI state for search and simple filters
  const [searchInputValue, setSearchInputValue] = useState('');
  const [datePreset, setDatePreset] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [actionItemsFilter, setActionItemsFilter] = useState(null);
  const [talkingPointsFilter, setTalkingPointsFilter] = useState(null);

  const debouncedSearchTerm = useDebounce(searchInputValue, 300);

  // Get store state and setters
  const {
    searchTerm,
    setSearchTerm,
    setDatePreset: setStoreDatePreset,
    setDateRange: setStoreDateRange,
    setActionItemsFilter: setStoreActionItemsFilter,
    setTalkingPointsFilter: setStoreTalkingPointsFilter,
    uiSearchInput,
    setUISearchInput,
  } = useMeetingsStoreRepository();

  // Sync UI search input from store on mount
  useEffect(() => {
    if (uiSearchInput && uiSearchInput !== searchInputValue) {
      setSearchInputValue(uiSearchInput);
    }
  }, [uiSearchInput]);

  // Keep store uiSearchInput in sync
  useEffect(() => {
    setUISearchInput(searchInputValue);
  }, [searchInputValue, setUISearchInput]);

  // Sync search term with debounce
  useEffect(() => {
    // Skip initial render when both terms are empty
    if (debouncedSearchTerm === '' && searchTerm === '' && searchInputValue === '') {
      return;
    }

    // Only proceed if the debounced term has actually changed
    if (debouncedSearchTerm === searchTerm) {
      return;
    }

    // Short delay to ensure UI updates before fetch operation
    const timer = setTimeout(() => {
      setSearchTerm(debouncedSearchTerm);
    }, 50);

    return () => clearTimeout(timer);
  }, [debouncedSearchTerm, searchTerm, searchInputValue, setSearchTerm]);

  // Sync date preset (skip if custom - custom uses date range instead)
  useEffect(() => {
    if (datePreset === MEETING_DATE_PRESETS.CUSTOM) {
      // Don't update store when selecting custom - wait for date range
      return;
    }
    // Clear custom dates when switching to a preset
    if (datePreset !== MEETING_DATE_PRESETS.CUSTOM) {
      setDateFrom(null);
      setDateTo(null);
    }
    setStoreDatePreset(datePreset);
  }, [datePreset, setStoreDatePreset]);

  // Sync custom date range
  useEffect(() => {
    if (datePreset !== MEETING_DATE_PRESETS.CUSTOM) {
      return;
    }
    // Only trigger API call when we have at least one date
    if (dateFrom || dateTo) {
      const fromStr = dateFrom ? dateFrom.format('YYYY-MM-DD') : null;
      const toStr = dateTo ? dateTo.format('YYYY-MM-DD') : null;
      setStoreDateRange(fromStr, toStr);
    }
  }, [dateFrom, dateTo, datePreset, setStoreDateRange]);

  // Sync action items filter
  useEffect(() => {
    setStoreActionItemsFilter(actionItemsFilter);
  }, [actionItemsFilter, setStoreActionItemsFilter]);

  // Sync talking points filter
  useEffect(() => {
    setStoreTalkingPointsFilter(talkingPointsFilter);
  }, [talkingPointsFilter, setStoreTalkingPointsFilter]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchInputValue('');
    setDatePreset(null);
    setDateFrom(null);
    setDateTo(null);
    setActionItemsFilter(null);
    setTalkingPointsFilter(null);
  }, []);

  return {
    // Values
    searchInputValue,
    datePreset,
    dateFrom,
    dateTo,
    actionItemsFilter,
    talkingPointsFilter,
    // Setters
    setSearchInputValue,
    setDatePreset,
    setDateFrom,
    setDateTo,
    setActionItemsFilter,
    setTalkingPointsFilter,
    clearFilters,
  };
};

export default useMeetingsRepositorySearch;
